import { computed, defineComponent, inject, nextTick, onBeforeUnmount, onMounted, reactive, ref, useContext, useCssModule, useRoute, watch, } from '@nuxtjs/composition-api';
import { throttle } from 'throttle-debounce-ts';
import { TRANSITIONS_DURATIONS } from './';
import { getElementTop, isElementInBounds, sendAnalytics } from '~/utils';
export default defineComponent({
    name: 'GallerySlider',
    props: {
        gallery: {
            required: true,
            type: Array,
        },
    },
    setup(props) {
        /**
         * Article.
         */
        const article = inject('article');
        const css = useCssModule();
        const context = useContext();
        const route = useRoute();
        const viewport = context.$viewport;
        const isMobile = computed(() => viewport.isLessThan('tablet'));
        const BOXES = reactive({
            lineClamp: 2,
            lineHeight: computed(() => (isMobile.value ? 22 : 24)),
        });
        const foldedCaptionHeight = computed(() => BOXES.lineHeight * BOXES.lineClamp);
        const currentSlideKey = ref(0);
        const currentSlideId = computed(() => props.gallery[currentSlideKey.value].id);
        const hasSlideInQuery = ref(false);
        const headerHeight = computed(() => (isMobile.value ? 88 : 60));
        const isCaptionUnfolded = ref(false);
        const isReady = ref(false);
        const rootEl = ref();
        const searchParams = ref('');
        const shouldShowDescriptions = ref(true);
        const slidesContainerEl = ref();
        const slidesObserver = ref();
        const slidesRef = ref([]);
        const slidesTimeout = ref(0);
        const slidesWithDescriptions = ref([]);
        const slidesWithReadMore = ref([]);
        const thumbsContainerEl = ref();
        const thumbsInBounds = ref([]);
        const thumbsObserver = ref();
        const thumbsOffsets = ref([]);
        const thumbsParent = computed(() => (isMobile.value ? null : thumbsContainerEl.value));
        const thumbsRef = ref([]);
        const thumbsTimeout = ref(0);
        const thumbsInBoundsLength = computed(() => {
            return thumbsInBounds.value.filter((isInViewport) => isInViewport).length;
        });
        const firstThumbIndexInViewport = ref(0);
        const lastThumbIndexInViewport = ref(thumbsInBoundsLength.value - 1);
        /**
         * Handle scroll
         */
        const handleScroll = throttle(500, () => {
            if (!rootEl.value || hasSlideInQuery.value) {
                return;
            }
            if (!isElementInBounds(rootEl.value)) {
                history.replaceState(null, '', `${location.pathname}?${searchParams.value}`);
            }
            else {
                history.replaceState(null, '', `?${searchParams.value}&slide=${currentSlideId.value}`);
            }
        });
        /**
         * Hanldes swipes on slides
         */
        async function handleSwipe(direction) {
            if (!['left', 'right'].includes(direction)) {
                return;
            }
            let nearestIndex;
            if (direction === 'left') {
                nearestIndex = currentSlideKey.value + 1;
                if (nearestIndex > slidesRef.value.length - 1) {
                    return;
                }
            }
            if (direction === 'right') {
                nearestIndex = currentSlideKey.value - 1;
                if (nearestIndex < 0) {
                    return;
                }
            }
            await scrollTo(String(nearestIndex));
        }
        /**
         * Handles custom switch article event
         */
        function handleSwitchArticle(event) {
            if (event.detail.startsWith(`/${article?.url_alias}`)) {
                return initGallery();
            }
            slidesObserver.value?.disconnect();
            thumbsObserver.value?.disconnect();
            window.removeEventListener('scroll', handleScroll);
        }
        /**
         * Initialize gallery
         */
        function initGallery() {
            for (let i = 0; i < props.gallery.length; i++) {
                slidesWithDescriptions.value.push(withDescription(props.gallery[i]));
                shouldShowDescriptions.value = slidesWithDescriptions.value.some((withDescription) => withDescription);
                const slide = slidesRef.value[i];
                const { scrollHeight = 0 } = slide.querySelector('[data-boxes-wrapper]') || {};
                const shouldShowReadMore = (scrollHeight || 0) > foldedCaptionHeight.value;
                slidesWithReadMore.value.push(shouldShowReadMore);
                const thumb = thumbsRef.value[i];
                const { left } = thumb.getBoundingClientRect();
                thumbsOffsets.value.push(left);
                thumbsInBounds.value.push(isElementInBounds(thumb, thumbsParent.value));
                slidesObserver.value?.observe(slide);
                thumbsObserver.value?.observe(thumb);
            }
            window.addEventListener('scroll', handleScroll);
        }
        /**
         * Initialize slides observer
         */
        function initSlidesObserver() {
            slidesObserver.value = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const { slideKey = '0' } = target.dataset;
                    const index = Number(slideKey);
                    clearTimeout(slidesTimeout.value);
                    // Auto scroll thumbs
                    slidesTimeout.value = window.setTimeout(async () => {
                        let left;
                        const shouldScrollForward = index >= lastThumbIndexInViewport.value;
                        const shouldScrollBackward = index <= firstThumbIndexInViewport.value;
                        if (shouldScrollForward || shouldScrollBackward) {
                            left = thumbsOffsets.value[index] - thumbsOffsets.value[firstThumbIndexInViewport.value];
                            if (shouldScrollBackward) {
                                left = -(thumbsOffsets.value[lastThumbIndexInViewport.value] - thumbsOffsets.value[index]);
                            }
                            thumbsContainerEl.value?.scrollBy({ left, behavior: 'smooth' });
                        }
                        currentSlideKey.value = index;
                        const path = `${route.value.path}?slide=${currentSlideId.value}`;
                        const url = context.env.HOST + path;
                        sendAnalytics.call(context, {
                            path,
                            url,
                        });
                        if (rootEl.value && isElementInBounds(rootEl.value) && !hasSlideInQuery.value) {
                            history.replaceState(null, '', `?${searchParams.value}&slide=${props.gallery[index].id}`);
                        }
                    }, TRANSITIONS_DURATIONS.observingSlides);
                }
            }, {
                threshold: 0.2,
            });
        }
        /**
         * Initialize thumbs observer
         */
        function initThumbsObserver() {
            thumbsObserver.value = new IntersectionObserver(() => {
                clearTimeout(thumbsTimeout.value);
                // Detect thumbs in viewport
                thumbsTimeout.value = window.setTimeout(() => {
                    thumbsInBounds.value = thumbsRef.value.map((thumb) => isElementInBounds(thumb, thumbsParent.value));
                    firstThumbIndexInViewport.value = thumbsInBounds.value.findIndex((isInViewport) => isInViewport);
                    lastThumbIndexInViewport.value = firstThumbIndexInViewport.value + thumbsInBoundsLength.value - 1;
                }, TRANSITIONS_DURATIONS.observingThumbs);
            }, {
                threshold: 1,
            });
        }
        /**
         * Scrolls to image
         */
        async function scrollTo(key) {
            await unfoldCaption();
            await scrollToRoot();
            const image = slidesRef.value?.find(({ dataset = {} }) => dataset?.slideKey === key);
            image?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            if (hasSlideInQuery.value) {
                delete route.value.query.slide;
                hasSlideInQuery.value = false;
                searchParams.value = new URLSearchParams(route.value.query).toString();
            }
        }
        /**
         * Scroll to root element
         */
        async function scrollToRoot() {
            if (rootEl.value && !isElementInBounds(rootEl.value)) {
                let { scrollToRoot, scrollIntoViewOverall } = TRANSITIONS_DURATIONS;
                const { scrollIntoView } = TRANSITIONS_DURATIONS;
                const top = getElementTop(rootEl.value) - headerHeight.value;
                scrollToRoot = Math.max(top / 2, scrollToRoot);
                await new Promise((resolve) => setTimeout(resolve, scrollToRoot));
                window.scroll({ top, behavior: 'smooth' });
                scrollIntoViewOverall = Math.max(props.gallery.length * scrollIntoView, scrollIntoViewOverall);
                await new Promise((resolve) => setTimeout(resolve, scrollIntoViewOverall));
            }
        }
        /**
         * Sets copyright
         */
        function setCopyright(item) {
            const { author, source } = item;
            return [author, source].filter(Boolean).join('<span>&middot;</span>');
        }
        /**
         * With description
         */
        function withDescription(item) {
            return Boolean(item.author || item.source || item.subtitle || (item.caption || []).length);
        }
        /**
         * Unfolds caption
         */
        async function unfoldCaption() {
            if (isCaptionUnfolded.value) {
                isCaptionUnfolded.value = false;
                await new Promise((resolve) => setTimeout(resolve, TRANSITIONS_DURATIONS.afterFolding));
            }
        }
        onBeforeUnmount(() => {
            slidesObserver.value?.disconnect();
            thumbsObserver.value?.disconnect();
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('switchArticle', handleSwitchArticle);
        });
        onMounted(async () => {
            const { query } = route.value || {};
            searchParams.value = new URLSearchParams(query).toString();
            const hasId = Boolean(props.gallery.find(({ id }) => Number(id) === Number(query.slide)));
            hasSlideInQuery.value = 'slide' in query && hasId;
            initSlidesObserver();
            initThumbsObserver();
            window.addEventListener('switchArticle', handleSwitchArticle);
            watch(isCaptionUnfolded, async (isCaptionUnfolded) => {
                if (!slidesContainerEl.value) {
                    return;
                }
                const boxesEl = slidesRef.value[currentSlideKey.value].querySelector('[data-boxes-wrapper]');
                if (!boxesEl) {
                    return;
                }
                const { scrollHeight, style } = boxesEl;
                await nextTick();
                if (isCaptionUnfolded) {
                    style.maxHeight = scrollHeight + 'px';
                    style.display = 'block';
                    slidesContainerEl.value.style.overflowX = 'hidden';
                }
                else {
                    style.maxHeight = '';
                    await new Promise((resolve) => setTimeout(resolve, TRANSITIONS_DURATIONS.folding));
                    style.display = '';
                    slidesContainerEl.value.style.overflowX = 'auto';
                }
            });
            initGallery();
            isReady.value = true;
            // TODO Оптимизировать доскрол на длинных статьях с кучей рекламы
            // if (hasSlideInQuery.value) {
            //   const slideIndex = slidesRef.value.findIndex(({ dataset }) => dataset.slideId === query.slide)
            //   await scrollTo(String(slideIndex))
            // }
        });
        return {
            BOXES,
            TRANSITIONS_DURATIONS,
            css,
            currentSlideId,
            currentSlideKey,
            firstThumbIndexInViewport,
            foldedCaptionHeight,
            handleSwipe,
            hasSlideInQuery,
            isCaptionUnfolded,
            isMobile,
            isReady,
            lastThumbIndexInViewport,
            rootEl,
            scrollTo,
            setCopyright,
            searchParams,
            shouldShowDescriptions,
            slidesContainerEl,
            slidesObserver,
            slidesRef,
            slidesWithDescriptions,
            slidesWithReadMore,
            thumbsContainerEl,
            thumbsInBounds,
            thumbsInBoundsLength,
            thumbsObserver,
            thumbsOffsets,
            thumbsRef,
        };
    },
});
